import apiV2 from '../api/v2';
import type { PracticeRiskStatus } from './practiceRisk';

// Accounts > Practices list. Backed by the existing admin GET /practices
// endpoint. Action items are cancelled as a feature, so this never gained
// openItems/overdue fields - openCalls/lastCallAt are the only per-row
// call-log signals, and both are now real.
export interface PracticeListItem {
    id: string;
    name: string;
    doctorName: string | null;
    drEmail: string | null;
    phone: string | null;
    email: string | null;
    city: string | null;
    state: string | null;
    createdAt: string;
    patientCount: number;
    openCalls: number;
    lastCallAt: string | null;
    // Rendered as-is when present, including the literal "Select" placeholder
    // value some rows genuinely have stored - that's real data, not an unset
    // marker, so it's never filtered out or special-cased.
    version: string | null;
    accountType: string | null;
    riskStatus: PracticeRiskStatus;
    riskNote: string | null;
}

export interface PaginatedMeta {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

// Global, unfiltered section-level counts (not derived from the current
// page or the current `hasOpenCalls` filter) - drives the stat cards above
// the (filterable) table, and the Accounts Overview hub's "Practices" card.
export interface PracticesListSummary {
    withOpenCalls: number;
    noActivity90d: number;
    addedThisMonth: number;
}

export interface PracticesListMeta extends PaginatedMeta {
    summary: PracticesListSummary;
}

export type PracticesSortBy = 'name' | 'doctor' | 'date' | 'patients';
export type SortOrder = 'asc' | 'desc';

export interface PracticesListParams {
    page: number;
    limit: number;
    q?: string;
    sort?: PracticesSortBy;
    order?: SortOrder;
    /** Filters to practices with at least one unresolved call. */
    hasOpenCalls?: boolean;
    /** Exact match against the practice's Monday-synced account type. */
    accountType?: string[];
    /** Exact match against practice.state. Abbreviation and full-name values
     * (e.g. "TX" and "Texas") exist as distinct rows server-side - never
     * merged here, just filtered on as-is. */
    state?: string[];
    /** Exact match against practice.city. ANDs with `state` server-side - a
     * practice in "Austin" AND state "TX" is a valid, narrower combination. */
    city?: string[];
    /** Exact match against practice.riskStatus - the closed
     * normal/warning/at_risk set (see services/practiceRisk.ts). */
    riskStatus?: PracticeRiskStatus[];
}

export interface PracticeFilterOptions {
    states: string[];
    accountTypes: string[];
    cities: string[];
}

export const fetchPractices = async (
    params: PracticesListParams
): Promise<{ data: PracticeListItem[]; meta: PracticesListMeta }> => {
    // Filter values stay arrays (not comma-joined here) - apiV2's paramsSerializer
    // escapes/joins them on the wire, matching the backend's parseListParam.
    const query: Record<string, string | string[]> = {
        page: String(params.page),
        limit: String(params.limit)
    };
    if (params.q) query.search = params.q;
    if (params.sort) query.sort = params.sort;
    if (params.order) query.order = params.order;
    if (params.hasOpenCalls) query.hasOpenCalls = 'true';
    if (params.accountType?.length) query.accountType = params.accountType;
    if (params.state?.length) query.state = params.state;
    if (params.city?.length) query.city = params.city;
    if (params.riskStatus?.length) query.riskStatus = params.riskStatus;

    const response = await apiV2.get<{
        success: boolean;
        data: PracticeListItem[];
        meta: PracticesListMeta;
    }>('/practices', { params: query });
    return { data: response.data.data, meta: response.data.meta };
};

export const fetchPracticeFilters = async (): Promise<PracticeFilterOptions> => {
    const response = await apiV2.get<{ success: boolean; data: PracticeFilterOptions }>(
        '/practices/filters'
    );
    return response.data.data;
};

/** Fields a practice profile is missing - drives the "Incomplete" row hint. */
export function missingPracticeFields(practice: PracticeListItem): string[] {
    const missing: string[] = [];
    if (!practice.doctorName) missing.push('doctor');
    if (!practice.city || !practice.state) missing.push('address');
    if (!practice.phone) missing.push('phone');
    if (!practice.email && !practice.drEmail) missing.push('email');
    return missing;
}
